import { Place, Trip } from "./interfaces";
import { Bounds } from "./position";
import { realDate } from "./time";
import { QueryParams, QueryParamsAndMutators } from "./urls";

/**
 * Optional overlays the user can switch on over any map.
 *
 * These are the same data sources the search sheet already offers as chips, surfaced as map layers
 * instead: chips only produce pins once you type something, so there was no way to simply *look* at
 * what's around you. A layer shows its source with no search term at all.
 *
 * - `myFavorites`      — your own saved places.
 * - `friendsFavorites` — saved places belonging to people you share trips with.
 * - `tripActivities`   — activities from trips. This is the master switch for the per-trip layers
 *                        below: individual trips are listed separately so you can browse one at a
 *                        time rather than getting an undifferentiated wash of "everywhere I've been".
 */
export type MapLayerId = 'myFavorites' | 'friendsFavorites' | 'tripActivities';

export type MapLayers = Record<MapLayerId, boolean>;

/**
 * Layers on by default show data you already own and expect to see. Friends' favorites is off by
 * default because it's other people's data — useful when you go looking for it, noise otherwise.
 */
export const DEFAULT_MAP_LAYERS: MapLayers = {
    myFavorites: true,
    friendsFavorites: false,
    tripActivities: true,
};

export const MAP_LAYER_LABELS: Record<MapLayerId, string> = {
    myFavorites: 'My favorites',
    friendsFavorites: "Friends' favorites",
    tripActivities: 'Trip activities',
};

/**
 * The zoom at which each layer starts drawing.
 *
 * Cutoffs exist because "show everything nearby" becomes "show everything, everywhere" as you zoom
 * out — thousands of pins on a map too coarse to tell them apart. But that pressure isn't equal
 * across layers, so the cutoff isn't either:
 *
 * - Favorites are a hand-curated set, small enough to draw at any zoom. Seeing them scattered across
 *   a continent is the *point* — that's the "where have I starred things" view. Fully global.
 * - Trip activities are per-day and unbounded: every meal and stop of every trip. Those need a real
 *   cutoff or they swamp the map (and the marker clusterer) the moment you zoom out.
 *
 * A layer below its cutoff stays switched on so it returns as you zoom in; the menu says why it
 * isn't drawing rather than leaving it looking broken.
 */
export const MAP_LAYER_MIN_ZOOM: Record<MapLayerId, number> = {
    myFavorites: 0,
    friendsFavorites: 0,
    tripActivities: 10,
};

/** The zoom at which trip activities (and so the "trips in view" list) become available. */
export const MIN_TRIP_ACTIVITY_ZOOM = MAP_LAYER_MIN_ZOOM.tripActivities;

/** Layers the user has switched on that aren't drawing yet because the map is zoomed too far out. */
export function layersBelowMinZoom(
    layers: MapLayers,
    available: MapLayerId[],
    zoomLevel: number | undefined
): MapLayerId[] {
    return available.filter(id => layers[id] && (zoomLevel ?? 0) < MAP_LAYER_MIN_ZOOM[id]);
}

const LAYERS_PARAM = 'layers';
const HIDDEN_TRIPS_PARAM = 'hideTrips';
const ALL_LAYER_IDS: MapLayerId[] = [ 'myFavorites', 'friendsFavorites', 'tripActivities' ];

function isLayerId(value: string): value is MapLayerId {
    return (ALL_LAYER_IDS as string[]).includes(value);
}

/**
 * Reads the enabled layers from the `layers=` param.
 *
 * Because layers have differing defaults, the param records *deviations* rather than the full state:
 * a bare name forces that layer on, a `-` prefix forces it off, and anything unmentioned keeps its
 * default. That keeps a default-state URL free of layer noise entirely.
 */
export function parseMapLayers(queryParams: QueryParams): MapLayers {
    const raw = queryParams.first(LAYERS_PARAM) ?? '';
    const layers = { ...DEFAULT_MAP_LAYERS };
    raw.split(',').map(part => part.trim()).filter(Boolean).forEach(part => {
        const isOff = part.startsWith('-');
        const name = isOff ? part.slice(1) : part;
        if (isLayerId(name)) {
            layers[name] = !isOff;
        }
    });
    return layers;
}

/** The trip ids the user has explicitly hidden. Trips in view are shown unless listed here. */
export function parseHiddenTrips(queryParams: QueryParams): Set<string> {
    const raw = queryParams.first(HIDDEN_TRIPS_PARAM) ?? '';
    return new Set(raw.split(',').map(part => part.trim()).filter(Boolean));
}

function encodeLayers(layers: MapLayers): string | undefined {
    const deviations = ALL_LAYER_IDS
        .filter(id => layers[id] !== DEFAULT_MAP_LAYERS[id])
        .map(id => (layers[id] ? id : `-${id}`));
    return deviations.length > 0 ? deviations.join(',') : undefined;
}

/**
 * Mirrors the layer state into the URL, so an overlay selection survives reload and back/forward.
 *
 * Uses 'replace' rather than 'push': toggling a layer isn't a navigation step you'd want to walk
 * back through one at a time. Note this is a *mirror*, not the source of truth — the router update
 * is deferred by a tick, so reading state back out of the URL would make rapid toggles race.
 */
export function writeMapLayers(
    queryParams: QueryParamsAndMutators,
    layers: MapLayers,
    hiddenTrips: Set<string>
) {
    const encodedLayers = encodeLayers(layers);
    const encodedHidden = Array.from(hiddenTrips).join(',');

    const toAppend: { [key: string]: string } = {};
    const toOmit: string[] = [];
    if (encodedLayers) {
        toAppend[LAYERS_PARAM] = encodedLayers;
    } else {
        toOmit.push(LAYERS_PARAM);
    }
    if (encodedHidden) {
        toAppend[HIDDEN_TRIPS_PARAM] = encodedHidden;
    } else {
        toOmit.push(HIDDEN_TRIPS_PARAM);
    }
    queryParams.replace(toAppend, toOmit);
}

/**
 * Which layers a given map can actually serve, derived from the chip sources it was configured with.
 * A map that doesn't search recent trips can't offer trip activities, and offering a toggle that
 * silently does nothing is worse than not offering it.
 */
export function availableMapLayers(
    chipConfig: { recents: boolean, savedPlaces: boolean }
): MapLayerId[] {
    const available: MapLayerId[] = [];
    if (chipConfig.savedPlaces) {
        available.push('myFavorites', 'friendsFavorites');
    }
    if (chipConfig.recents) {
        available.push('tripActivities');
    }
    return available;
}

function isWithinBounds(place: Pick<Place, 'lat' | 'lng'>, bounds: Bounds): boolean {
    const { lat, lng } = place;
    if (lat === null || lat === undefined || lng === null || lng === undefined) {
        return false;
    }
    if (lat < bounds.southWest.lat || lat > bounds.northEast.lat) {
        return false;
    }
    // A viewport straddling the antimeridian has southWest.lng > northEast.lng, so the longitude
    // test has to wrap rather than being a plain range check.
    return bounds.southWest.lng <= bounds.northEast.lng
        ? lng >= bounds.southWest.lng && lng <= bounds.northEast.lng
        : lng >= bounds.southWest.lng || lng <= bounds.northEast.lng;
}

export type TripInView = {
    tripId: string;
    title: string;
    /** Latest activity date in view, for ordering. Undefined for trips with no real dates. */
    date: ReturnType<typeof realDate>;
    activityCount: number;
};

/**
 * The trips that have at least one positioned activity inside the current viewport.
 *
 * This is a plain in-memory scan of the activities we already hold in AllTripActivitiesContext — no
 * query, no round trip — so it's cheap enough to redo as the map moves. It's what lets the layers
 * menu name actual trips ("Vienna, Nov 2024") instead of offering an opaque "prior visits" switch.
 *
 * Ordered most recent first, with undated trips last.
 */
export function tripsInView(
    activities: Place[],
    trips: Trip[],
    bounds: Bounds | undefined
): TripInView[] {
    if (!bounds) {
        return [];
    }

    const titlesById = new Map(trips.map(trip => [ trip.docid ?? '', trip ]));
    const found = new Map<string, TripInView>();

    activities.forEach(activity => {
        const tripId = activity.tripdocid;
        if (!tripId || !isWithinBounds(activity, bounds)) {
            return;
        }
        const existing = found.get(tripId);
        const activityDate = realDate(activity.startdatetime);
        if (!existing) {
            const trip = titlesById.get(tripId);
            found.set(tripId, {
                tripId,
                title: trip?.title ?? trip?.location ?? 'Untitled trip',
                date: activityDate,
                activityCount: 1,
            });
        } else {
            existing.activityCount++;
            if (activityDate && (!existing.date || activityDate.isAfter(existing.date))) {
                existing.date = activityDate;
            }
        }
    });

    return Array.from(found.values()).sort((a, b) => {
        if (!a.date && !b.date) return a.title.localeCompare(b.title);
        if (!a.date) return 1;
        if (!b.date) return -1;
        return b.date.compareTo(a.date);
    });
}
